import cn from 'classnames'
import { useEffect, useRef } from 'react'
import { Link } from 'react-router'

import { claimNavigationFocus } from '@/app/App/hook/useFocus'

import type { BaseTabsProps } from '../Tabs'
import styles from './NavLinkItems.module.scss'

export type NavLinkItem<T extends string> = {
  /**
   * The label of the nav link.
   */
  label: React.ReactNode
  /**
   * The unique key identifying the nav link.
   */
  key: T
  /**
   * The URL for the navigation link.
   */
  url: string
}

type NavLinkItemsProps<T extends string> = BaseTabsProps<T> & {
  links: NavLinkItem<T>[]
}

export const NavLinkItems = <T extends string>({
  navLabel,
  selectedKey,
  links,
  className,
}: NavLinkItemsProps<T>): JSX.Element => {
  const selectedLinkRef = useRef<HTMLAnchorElement>(null)

  // Moves focus to the selected tab whenever it (re)appears, e.g. once its
  // data has finished loading, so screen reader users land on it after
  // navigating to the next/previous step.
  useEffect(() => {
    claimNavigationFocus(selectedLinkRef.current)
  }, [selectedKey])

  return (
    <nav aria-label={navLabel}>
      {/** biome-ignore lint/correctness/useUniqueElementIds: This is always
          rendered once per page, so there cannot be id duplications.> */}
      <ul id="tablist" className={cn(styles['menu-list'], className)}>
        {links.map(({ key, label, url }) => {
          const isSelected = selectedKey === key

          return (
            <li
              className={styles['menu-item']}
              {...(isSelected ? { id: 'selected' } : {})}
              key={key}
            >
              <Link
                ref={isSelected ? selectedLinkRef : undefined}
                to={url}
                className={cn(styles['menu-list-item'], {
                  [styles['is-selected']]: isSelected,
                })}
              >
                <span>
                  {isSelected && (
                    <span className={styles['visually-hidden']}>
                      Lien actif
                    </span>
                  )}
                  <span>{label}</span>
                </span>
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
